'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import type { ShopperProducts } from '@/scapi';
import type { ComponentWithComponentData } from '@/lib/page-designer/component-loader.server';
import { hydrateEmbeddedComponentData } from './embedded-component';
import {
    MEGA_MENU_NAVIGATION_RESOURCE,
    type MegaMenuNavigationLoaderData,
    type MegaMenuTrendingContent,
} from './mega-menu-loader-data';

type UseMegaMenuContentOptions = {
    /** Prefetch when the desktop mega menu bar is visible (custom labels). */
    enabled: boolean;
    /** Dropdown is open — create panel promises and allow one retry after a failed prefetch. */
    open: boolean;
};

type UseMegaMenuContentResult = {
    brandsCategoryPromise: Promise<ShopperProducts.schemas['Category']> | undefined;
    newInCategoryPromise: Promise<ShopperProducts.schemas['Category']> | undefined;
    trendingContentPromise: Promise<MegaMenuTrendingContent> | undefined;
    megaMenuEmbedded: ComponentWithComponentData | undefined;
    isLoading: boolean;
    hasLoaded: boolean;
};

type LoadAttempt = 'prefetch' | 'open';

export function useMegaMenuContent({ enabled, open }: UseMegaMenuContentOptions): UseMegaMenuContentResult {
    const fetcher = useFetcher<MegaMenuNavigationLoaderData>({ key: 'mega-menu-navigation' });
    const attemptedKeyRef = useRef<LoadAttempt | null>(null);
    const brandsResolveRef = useRef<((category: ShopperProducts.schemas['Category']) => void) | null>(null);
    const newInResolveRef = useRef<((category: ShopperProducts.schemas['Category']) => void) | null>(null);
    const trendingResolveRef = useRef<((content: MegaMenuTrendingContent) => void) | null>(null);
    const [brandsCategoryPromise, setBrandsCategoryPromise] = useState<
        Promise<ShopperProducts.schemas['Category']> | undefined
    >();
    const [newInCategoryPromise, setNewInCategoryPromise] = useState<
        Promise<ShopperProducts.schemas['Category']> | undefined
    >();
    const [trendingContentPromise, setTrendingContentPromise] = useState<
        Promise<MegaMenuTrendingContent> | undefined
    >();

    useEffect(() => {
        if (!enabled || fetcher.data || fetcher.state !== 'idle') {
            return;
        }

        const nextAttempt: LoadAttempt = open ? 'open' : 'prefetch';
        if (attemptedKeyRef.current === 'open') {
            return;
        }
        if (attemptedKeyRef.current === 'prefetch' && nextAttempt === 'prefetch') {
            return;
        }

        attemptedKeyRef.current = nextAttempt;
        void fetcher.load(MEGA_MENU_NAVIGATION_RESOURCE);
    }, [enabled, open, fetcher, fetcher.data, fetcher.state, fetcher.load]);

    useEffect(() => {
        if (fetcher.data?.brandsCategory && brandsResolveRef.current) {
            brandsResolveRef.current(fetcher.data.brandsCategory);
            brandsResolveRef.current = null;
        }
        if (fetcher.data?.newInCategory && newInResolveRef.current) {
            newInResolveRef.current(fetcher.data.newInCategory);
            newInResolveRef.current = null;
        }
        if (fetcher.data?.trendingContent && trendingResolveRef.current) {
            trendingResolveRef.current(fetcher.data.trendingContent);
            trendingResolveRef.current = null;
        }
    }, [fetcher.data?.brandsCategory, fetcher.data?.newInCategory, fetcher.data?.trendingContent]);

    useEffect(() => {
        if (!open) {
            setBrandsCategoryPromise(undefined);
            setNewInCategoryPromise(undefined);
            setTrendingContentPromise(undefined);
            brandsResolveRef.current = null;
            newInResolveRef.current = null;
            trendingResolveRef.current = null;
            return;
        }

        if (fetcher.data?.brandsCategory) {
            setBrandsCategoryPromise(Promise.resolve(fetcher.data.brandsCategory));
            brandsResolveRef.current = null;
        } else {
            setBrandsCategoryPromise(
                new Promise<ShopperProducts.schemas['Category']>((resolve) => {
                    brandsResolveRef.current = resolve;
                })
            );
        }

        if (fetcher.data?.newInCategory) {
            setNewInCategoryPromise(Promise.resolve(fetcher.data.newInCategory));
            newInResolveRef.current = null;
        } else {
            setNewInCategoryPromise(
                new Promise<ShopperProducts.schemas['Category']>((resolve) => {
                    newInResolveRef.current = resolve;
                })
            );
        }

        if (fetcher.data?.trendingContent) {
            setTrendingContentPromise(Promise.resolve(fetcher.data.trendingContent));
            trendingResolveRef.current = null;
        } else {
            setTrendingContentPromise(
                new Promise<MegaMenuTrendingContent>((resolve) => {
                    trendingResolveRef.current = resolve;
                })
            );
        }
    }, [open, fetcher.data?.brandsCategory, fetcher.data?.newInCategory, fetcher.data?.trendingContent]);

    const megaMenuEmbedded = useMemo(
        () =>
            fetcher.data?.megaMenuEmbedded ? hydrateEmbeddedComponentData(fetcher.data.megaMenuEmbedded) : undefined,
        [fetcher.data?.megaMenuEmbedded]
    );

    return useMemo(
        () => ({
            brandsCategoryPromise,
            newInCategoryPromise,
            trendingContentPromise,
            megaMenuEmbedded,
            isLoading: open && !fetcher.data && fetcher.state !== 'idle',
            hasLoaded: Boolean(fetcher.data),
        }),
        [
            brandsCategoryPromise,
            newInCategoryPromise,
            trendingContentPromise,
            megaMenuEmbedded,
            open,
            fetcher.data,
            fetcher.state,
        ]
    );
}
